// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Accumulates a live series across polls so a measured curve grows rather than
 * being re-fetched whole.
 *
 * The Live view used to ask for `[axisStart, now]` every minute — by late
 * afternoon that is sixty 15-minute buckets of which fifty-nine are already
 * known. Each poll now asks only for the tail and merges it into what the cache
 * already holds.
 *
 * Merging happens on **raw points, before {@link resampleToAxis}**. That
 * function averages the points that fall in each bucket, so merging two already
 * resampled arrays would average an average and skew any bucket whose points
 * arrived across two fetches. Resampling stays a pure derivation over the full
 * merged set.
 *
 * {@link mergeComposedWindow} does the same job for the BFF-composed endpoints,
 * where the payload is one shared axis plus a tree of arrays parallel to it, and
 * adds the coverage rules a fan-out-behind-a-200 needs.
 */

import type { ResamplePoint } from './telemetryResample';
import type { MetricSeries } from '../types';

export interface WindowedMetricSeries {
  series: MetricSeries[];
  /** ISO-8601 end of the widest window merged into `series` so far. */
  fetchedThrough: string;
}

/**
 * Fold a freshly fetched window into the accumulated series.
 *
 * Rows collide on their ISO timestamp and **the newer response wins**: the
 * bucket at the leading edge was partial when it was first read, so re-asking
 * for it and overwriting is what lets it converge instead of freezing
 * half-complete. The same rule absorbs any upstream backfill.
 *
 * A metric present only in `prev` survives. Upstream may legitimately omit a
 * metric from a narrow window simply because nothing was measured in it, and
 * that is not evidence the metric has gone away.
 *
 * Rows older than `dropBefore` (the plan axis start) are pruned, so an entry
 * held open all day cannot grow past the axis it will be drawn on.
 */
export function mergeWindowedSeries(
  prev: WindowedMetricSeries | undefined,
  next: MetricSeries[],
  window: { end: string; dropBefore: string }
): WindowedMetricSeries {
  const { end, dropBefore } = window;
  const byName = new Map<string, MetricSeries>();

  for (const series of prev?.series ?? []) {
    byName.set(series.name, series);
  }

  for (const fresh of next) {
    const existing = byName.get(fresh.name);
    if (!existing) {
      byName.set(fresh.name, fresh);
      continue;
    }
    const rows = new Map(existing.data.map(row => [row.key, row]));
    for (const row of fresh.data) rows.set(row.key, row);
    byName.set(fresh.name, {
      ...existing,
      unit: fresh.unit || existing.unit,
      data: Array.from(rows.values()),
    });
  }

  const floor = Date.parse(dropBefore);
  const series = Array.from(byName.values()).map(entry => ({
    ...entry,
    data: entry.data
      .filter(row => !(Date.parse(row.key) < floor))
      .sort((a, b) => Date.parse(a.key) - Date.parse(b.key)),
  }));

  return {
    series,
    fetchedThrough: laterOf(prev?.fetchedThrough, end),
  };
}

function laterOf(a: string | undefined, b: string): string {
  if (!a) return b;
  return Date.parse(a) > Date.parse(b) ? a : b;
}

export interface WindowedPoints {
  points: ResamplePoint[];
  unit?: string;
  /** ISO-8601 end of the widest window merged into `points` so far. */
  fetchedThrough: string;
}

/**
 * {@link mergeWindowedSeries} for the weather observations shape, which is a
 * flat point list rather than one list per metric. Same rules: newer wins on a
 * timestamp collision, rows before `dropBefore` are pruned, `fetchedThrough`
 * only ever moves forward.
 */
export function mergeWindowedPoints(
  prev: WindowedPoints | undefined,
  next: { points: ResamplePoint[]; unit?: string },
  window: { end: string; dropBefore: string }
): WindowedPoints {
  const rows = new Map((prev?.points ?? []).map(point => [point.timestamp, point]));
  for (const point of next.points) rows.set(point.timestamp, point);

  const floor = Date.parse(window.dropBefore);
  const points = Array.from(rows.values())
    .filter(point => !(Date.parse(point.timestamp) < floor))
    .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));

  return {
    points,
    unit: next.unit ?? prev?.unit,
    fetchedThrough: laterOf(prev?.fetchedThrough, window.end),
  };
}

/** The parts of a composed payload {@link mergeComposedWindow} reads. */
export interface ComposedShape {
  timestamps: string[];
  charts: unknown;
  coverage: { requested: number; contributing: number; failed?: string[] };
}

export interface ComposedWindow<TPayload> {
  /** The accumulated payload, in the same shape the endpoint returned. */
  payload: TPayload;
  /**
   * The `coverage.contributing` of the poll that last wrote each row of
   * `payload.timestamps`. A later poll that saw fewer members cannot lower a
   * row this one already filled.
   */
  quality: number[];
  /** ISO-8601 end of the widest window merged into `payload` so far. */
  fetchedThrough: string;
  /**
   * Members the *latest* poll could not read, when that poll saw fewer than
   * what is currently drawn. The curve on screen is partly the older, better
   * reading, and a card that discloses that has to read this.
   */
  degraded: string[];
}

function collectSeries(
  node: unknown,
  length: number,
  path: string,
  out: Map<string, (number | null)[]>
): void {
  if (length === 0 || !node || typeof node !== 'object') return;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    const next = path ? `${path}.${key}` : key;
    if (Array.isArray(value)) {
      if (value.length === length) out.set(next, value as (number | null)[]);
      continue;
    }
    if (value && typeof value === 'object') collectSeries(value, length, next, out);
  }
}

function rebuildCharts(
  node: unknown,
  path: string,
  merged: Map<string, (number | null)[]>
): unknown {
  if (!node || typeof node !== 'object') return node;
  const out: Record<string, unknown> = { ...(node as Record<string, unknown>) };
  for (const [key, value] of Object.entries(out)) {
    const next = path ? `${path}.${key}` : key;
    if (Array.isArray(value)) {
      const replacement = merged.get(next);
      if (replacement) out[key] = replacement;
      continue;
    }
    if (value && typeof value === 'object') out[key] = rebuildCharts(value, next, merged);
  }
  return out;
}

/**
 * {@link mergeWindowedSeries} for the *composed* endpoints, whose payload is a
 * shared timestamp axis plus a tree of arrays parallel to it.
 *
 * These endpoints fan out across nine chillers or thirty-six HVAC devices in the
 * BFF, and a per-device 503 is absorbed there rather than raised: the response
 * is a 200 whose sum is simply short, or empty. Replacing the cached payload
 * with that is what used to wipe the measured curve off the card once a minute.
 * Two rules stop it:
 *
 *   - **Row gate.** A poll that saw fewer members than the one that wrote a row
 *     may fill that row's gaps but may not overwrite what is already there. A
 *     poll where nothing answered therefore changes nothing.
 *   - **Null guard.** A fresh `null` never clears an existing value, at any
 *     coverage. The leading-edge bucket still converges upward on the re-read;
 *     it just cannot converge to absent.
 *
 * The consequence, and the accepted trade: a value measured once is drawn for
 * the rest of the plan day even if later reads no longer see it. Rows the
 * degraded polls never reach — the new ones at the leading edge — do carry the
 * short sum, which is honest, and `degraded` is how the card says so.
 *
 * Returns `prev` untouched when there is nothing to merge, so a caller can hand
 * over an empty response without special-casing it.
 */
export function mergeComposedWindow<TPayload extends ComposedShape>(
  prev: ComposedWindow<TPayload> | undefined,
  next: TPayload | undefined,
  window: { end: string; dropBefore: string }
): ComposedWindow<TPayload> | null {
  if (!next) return prev ?? null;

  const paths = new Set<string>();
  const rows = new Map<string, { values: Map<string, number | null>; quality: number }>();

  if (prev) {
    const prevSeries = new Map<string, (number | null)[]>();
    collectSeries(prev.payload.charts, prev.payload.timestamps.length, '', prevSeries);
    prevSeries.forEach((_series, path) => paths.add(path));
    prev.payload.timestamps.forEach((timestamp, index) => {
      const values = new Map<string, number | null>();
      prevSeries.forEach((array, path) => values.set(path, array[index] ?? null));
      rows.set(timestamp, { values, quality: prev.quality[index] ?? 0 });
    });
  }

  const freshSeries = new Map<string, (number | null)[]>();
  collectSeries(next.charts, next.timestamps.length, '', freshSeries);
  freshSeries.forEach((_series, path) => paths.add(path));
  const freshQuality = next.coverage?.contributing ?? 0;

  next.timestamps.forEach((timestamp, index) => {
    const existing = rows.get(timestamp);
    if (!existing) {
      const values = new Map<string, number | null>();
      freshSeries.forEach((array, path) => values.set(path, array[index] ?? null));
      rows.set(timestamp, { values, quality: freshQuality });
      return;
    }
    const degradedRow = freshQuality < existing.quality;
    freshSeries.forEach((array, path) => {
      const fresh = array[index] ?? null;
      if (fresh === null) return;
      if (degradedRow && existing.values.get(path) != null) return;
      existing.values.set(path, fresh);
    });
    if (!degradedRow) existing.quality = freshQuality;
  });

  const floor = Date.parse(window.dropBefore);
  const ordered = Array.from(rows.entries())
    .filter(([timestamp]) => !(Date.parse(timestamp) < floor))
    .sort((a, b) => Date.parse(a[0]) - Date.parse(b[0]));

  const merged = new Map<string, (number | null)[]>();
  paths.forEach(path => {
    merged.set(
      path,
      ordered.map(([, row]) => row.values.get(path) ?? null)
    );
  });

  const retained = prev?.payload.coverage;
  const best =
    retained && retained.contributing > next.coverage.contributing ? retained : next.coverage;

  return {
    payload: {
      ...next,
      timestamps: ordered.map(([timestamp]) => timestamp),
      charts: rebuildCharts(next.charts, '', merged),
      coverage: best,
      // The spread reproduces `next`'s own shape; `charts` is only structurally
      // typed here because the merge walks it generically.
    } as TPayload,
    quality: ordered.map(([, row]) => row.quality),
    fetchedThrough: laterOf(prev?.fetchedThrough, window.end),
    degraded:
      retained && next.coverage.contributing < retained.contributing
        ? (next.coverage.failed ?? [])
        : [],
  };
}
